/**
 * QMS Users Hooks
 * TanStack Query hooks for fetching and searching users for QMS ticket assignment
 */

import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/hooks/useSession';
import { fetchQmsUsers, fetchQmsUserById } from '../api/qmsApi';
import { getQmsOrganisationId } from '../utils/organisation';

// Query keys factory
export const qmsUsersKeys = {
  all: ['qms-users'] as const,
  search: (orgId: string, search: string) =>
    [...qmsUsersKeys.all, 'search', orgId, search] as const,
  detail: (userId: string) => [...qmsUsersKeys.all, 'detail', userId] as const,
};

/**
 * Hook for searching users with debounced search support
 * Used by QmsUserSelect component
 *
 * Note: Fetches ATEK internal users for ticket assignment.
 * Empty search returns all users, non-empty search filters by name/email.
 */
export function useQmsUserSearch(search: string, enabled: boolean = true) {
  const { user } = useSession();
  const organizationId = getQmsOrganisationId(user?.organisation);

  return useQuery({
    queryKey: qmsUsersKeys.search(organizationId, search),
    queryFn: () => fetchQmsUsers(organizationId, search),
    enabled: !!organizationId && enabled, // Allow empty search to show all users
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for fetching a single user by ID
 * Used to display selected user when only ID is available
 */
export function useQmsUserById(userId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: qmsUsersKeys.detail(userId || ''),
    queryFn: () => fetchQmsUserById(userId!),
    enabled: !!userId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - user data doesn't change often
  });
}
